import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material'
import SecurityIcon from '@mui/icons-material/Security'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import BusinessIcon from '@mui/icons-material/Business'
import { certificateApi, awsCertAccountApi, itsmProductApi, productDlApi } from '../utils/api'
import AccountOnboard from './AccountOnboard'
import ProductOnboard from './ProductOnboard'
import {
  CertificateStatusStep,
  VenafiCertLookupStep,
  DeploymentLocationStep,
  AwsConfigurationStep,
  OnPremConfigurationStep,
  ContactCertInfoStep,
  MtlsLayer7Step,
  AwsStorageOptionsStep,
} from '../components/onboard'

const CertificateOnboard = () => {
  const [formData, setFormData] = useState({
    // Shared fields
    preExisting: '',
    isAWS: '',
    productCode: '',
    productName: '',
    environment: '',
    emailId: '',
    clientInvolvement: '',
    clientName: '',
    dependencyPresent: '',
    dependencies: [''],
    certificateUsage: '',
    itsmGroup: '',
    venafiPolicyBase: '',
    commonName: '',
    venafiPath: '',
    sanEntries: [''],
    mutualAuth: '',
    layer7: '',
    layer7Endpoint: '',
    
    // AWS-specific fields
    awsAdditionalFields: {
      accountName: '',
      accountNumber: '',
      applicationId: '',
      awsCertInOnprem: 'no',
      acmParameter: 'no',
      existingAcmArn1: '',
      existingAcmArn2: '',
      ssmParameter: 'no',
      ssmFormat: '',
      ssmCertPath: '',
      ssmPrivateKeyPath: '',
      ssmChainKeyPath: '',
      ssmCertPasswordPath: '',
      ssmCertContentType: '',
      ssmCertParamType: '',
      ssmKeyContentType: '',
      ssmKeyParamType: '',
      ssmChainContentType: '',
      ssmChainParamType: '',
      ssmPasswordContentType: '',
      ssmPasswordParamType: '',
      smParameter: 'no',
      smFormat: '',
      smCertPath: '',
    },
    
    // OnPrem-specific fields (for mTLS only)
    onpremAdditionalFields: {
      publicCertKeyLocation: '',
      privateCertKeyLocation: '',
    },
  })

  const [awsAccounts, setAwsAccounts] = useState([])
  const [products, setProducts] = useState([])
  const [itsmProducts, setItsmProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null) // Store full product object
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [productSearchText, setProductSearchText] = useState('')
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [availableEnvironments, setAvailableEnvironments] = useState(['dev', 'qa', 'uat', 'prd'])
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [venafiLookup, setVenafiLookup] = useState({
    status: 'idle',
    exists: false,
    matches: [],
    truncated: false,
    error: '',
    selectedMatch: null,
    certNotActive: false,
    alreadyOnboarded: false,
  })
  const venafiLookupRef = useRef({ lastQuery: '' })
  const certDetailsCache = useRef({}) // keyed by guid; cleared on CN change / preExisting toggle / onboard
  const skipCommonNameResetRef = useRef(false) // suppresses CN-change effect when selectVenafiMatch updates it programmatically

  // Duplicate check state for new cert common name
  const [commonNameOnboarded, setCommonNameOnboarded] = useState({ checked: false, onboarded: false, message: '' })
  
  // Modal state for AWS Account Onboarding
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedProductForModal, setSelectedProductForModal] = useState(null)
  const [useAvailableAccountsOnly, setUseAvailableAccountsOnly] = useState(false)

  // Modal state for Product Onboarding
  const [productOnboardModalOpen, setProductOnboardModalOpen] = useState(false)
  const [selectedOnpremProduct, setSelectedOnpremProduct] = useState(null)
  const [onpremProductExists, setOnpremProductExists] = useState(null) // null = not checked, true = exists, false = doesn't exist

  // Common name validation removed - no longer tied to certificate usage type
  const validateCommonName = () => {
    return { isValid: true, message: '' }
  }

  const resetVenafiLookup = () => {
    certDetailsCache.current = {} // dump cached details whenever the search is reset
    setVenafiLookup({
      status: 'idle',
      exists: false,
      matches: [],
      truncated: false,
      error: '',
      selectedMatch: null,
      certNotActive: false,
      alreadyOnboarded: false,
    })
  }

  // Clear only the selection — keeps the match list so the user can re-pick
  const clearVenafiSelection = () => {
    setVenafiLookup(prev => ({ ...prev, selectedMatch: null, status: 'success' }))
    // Also clear the fields that were auto-populated from the selection
    setFormData(prev => ({
      ...prev,
      environment: '',
      productCode: '',
      productName: '',
      clientName: '',
      certificateUsage: '',
      sanEntries: [''],
      venafiPath: '',
      emailId: prev._originalEmailId || prev.emailId,
    }))
    setSelectedProduct(null)
  }

  const recheckVenafiMatch = () => {
    const match = venafiLookup.selectedMatch
    if (!match) return
    // Bust the cache so we get fresh data from Venafi
    const cacheKey = match.guid || match.certificate_dn
    delete certDetailsCache.current[cacheKey]
    selectVenafiMatch(match)
  }

  // Given a full Venafi policy_dn such as
  //   \VED\Policy\Non-Production\BRCC DigiCert Standard CA\SRE-SD\DEP
  // return only the portion AFTER the CA anchor folder, e.g. 'SRE-SD\DEP'.
  // Falls back to '' if no known CA anchor is found.
  const extractSubPath = (policyDn) => {
    if (!policyDn) return ''
    const CA_ANCHORS = ['BRCC DigiCert Standard CA', 'BRCC Entrust Private Gateway CA']
    const parts = policyDn.split('\\').filter(p => p)
    const caIdx = parts.findIndex(p => CA_ANCHORS.some(ca => p.toLowerCase() === ca.toLowerCase()))
    if (caIdx === -1) return ''
    let afterCa = parts.slice(caIdx + 1)
    // Skip the Automation folder itself if the cert is already under it
    if (afterCa.length > 0 && afterCa[0].toLowerCase() === 'automation') {
      afterCa = afterCa.slice(1)
    }
    return afterCa.join('\\')
  }

  const selectVenafiMatch = async (match) => {
    const cacheKey = match.guid || match.certificate_dn

    // ── Step 1: Always clear all Venafi-sourced form fields immediately ───────
    // Prevents any cross-cert state bleed while the new cert's data loads.
    setFormData(prev => ({
      ...prev,
      environment: '',
      productCode: '',
      productName: '',
      clientName: '',
      certificateUsage: '',
      sanEntries: [''],
      venafiPath: '',
      emailId: prev._originalEmailId || prev.emailId,
    }))
    setSelectedProduct(null)

    // ── Step 2: Show loading state ────────────────────────────────────────────
    setVenafiLookup(prev => ({
      ...prev,
      selectedMatch: match,
      status: 'loading',
    }))

    // ── Step 3: Get raw Venafi data — from cache or live API ─────────────────
    // Cache stores ONLY the raw API response. No computed/derived values.
    let rawDetails = certDetailsCache.current[cacheKey]
    if (!rawDetails) {
      try {
        const detailsResponse = await certificateApi.getVenafiCertificateDetails(match.certificate_dn, match.guid)
        rawDetails = detailsResponse.data || {}
        certDetailsCache.current[cacheKey] = rawDetails // raw only — never user-modified state
      } catch (error) {
        console.error('Failed to fetch certificate details:', error)
        setVenafiLookup(prev => ({ ...prev, status: 'success', selectedMatch: match }))
        setFormData(prev => ({ ...prev, commonName: match.common_name || prev.commonName, venafiPath: extractSubPath(match.policy_dn), sanEntries: [''] }))
        return
      }
    }

    // ── Step 4: Detect empty CertificateDetails (cert never renewed / not active) ─
    const isEmptyDetails = !rawDetails.common_name && !rawDetails.valid_from && !rawDetails.valid_to && !rawDetails.thumbprint
    if (isEmptyDetails) {
      setVenafiLookup(prev => ({
        ...prev,
        selectedMatch: match,
        status: 'success',
        certNotActive: true,
      }))
      return
    }

    // ── Step 5: Destructure raw data ──────────────────────────────────────────
    const {
      sans = [], common_name, client_name, certificate_usage,
      valid_from, valid_to, key_algorithm, key_size, thumbprint, issuer, serial_number, consumers = [],
      environment: venafiEnv, application_name: venafiAppName,
      requestor: venafiRequestor, contact_email: venafiContactEmail,
    } = rawDetails

    const finalSans = sans.length > 0 ? sans.filter(s => s.trim() !== '') : ['']

    // ── Step 5: Compute derived values fresh from raw data only ───────────────
    // Never read from formData here — it would capture stale values from the
    // previously selected cert.
    const autoEnv = venafiEnv || ''

    let autoProductCode = ''
    let autoProductName = ''
    if (venafiAppName) {
      let matchedProduct = itsmProducts.find(
        p =>
          p.product_name?.toLowerCase() === venafiAppName.toLowerCase() ||
          p.product_code?.toLowerCase() === venafiAppName.toLowerCase()
      )

      // If not found in the already-loaded list, do a targeted API search
      if (!matchedProduct) {
        try {
          const searchResp = await itsmProductApi.getAll({ search: venafiAppName, limit: 100 })
          const searchResults = searchResp.data.items || []
          matchedProduct = searchResults.find(
            p =>
              p.product_name?.toLowerCase() === venafiAppName.toLowerCase() ||
              p.product_code?.toLowerCase() === venafiAppName.toLowerCase()
          )
          if (matchedProduct) {
            // Merge into the loaded list so the Autocomplete can see it
            setItsmProducts(prev => {
              const exists = prev.some(p => p.product_code === matchedProduct.product_code)
              return exists ? prev : [...prev, matchedProduct]
            })
          }
        } catch (err) {
          console.error('Targeted product search failed:', err)
        }
      }

      if (matchedProduct) {
        autoProductCode = matchedProduct.product_code
        autoProductName = matchedProduct.product_name
        setSelectedProduct(matchedProduct)
      }
    }

    // ── Step 6: Populate form entirely from this cert's Venafi data ───────────
    // Suppress the commonName-change useEffect so it doesn't reset the Venafi lookup
    // state we are about to set in Step 8 below.
    skipCommonNameResetRef.current = true
    setFormData(prev => ({
      ...prev,
      commonName: common_name || match.common_name || prev.commonName,
      venafiPath: extractSubPath(match.policy_dn),
      sanEntries: finalSans,
      clientName: client_name || 'N/A',
      certificateUsage: certificate_usage || '',
      environment: autoEnv,
      productCode: autoProductCode,
      productName: autoProductName,
      emailId: venafiContactEmail || prev._originalEmailId || prev.emailId,
    }))

    // ── Step 8: Store enriched selectedMatch (Venafi raw fields only) ─────────
    setVenafiLookup(prev => ({
      ...prev,
      selectedMatch: {
        ...match,
        sans,
        cert_common_name: common_name,
        client_name,
        certificate_usage,
        valid_from, valid_to, key_algorithm, key_size,
        thumbprint, issuer, serial_number, consumers,
        environment: venafiEnv,
        application_name: venafiAppName,
        requestor: venafiRequestor,
        contact_email: venafiContactEmail,
      },
      status: 'success',
      certNotActive: false,
    }))
  }

  const checkVenafiCommonName = async () => {
    // For new certs, only check if called explicitly; for pre-existing, always check.
    // The new VenafiCertLookupStep only renders when preExisting === 'true',
    // and ContactCertInfoStep only calls this for new certs (no-op there).
    if (formData.preExisting !== 'true') return

    const commonName = formData.commonName.trim()
    if (!commonName) return

    // No dedup guard — user explicitly clicked Search, always run the query

    setVenafiLookup({
      status: 'loading',
      exists: false,
      matches: [],
      truncated: false,
      certNotActive: false,
      alreadyOnboarded: false,
      error: '',
      selectedMatch: null
    })

    // Check our DB first — if already onboarded, don't bother calling Venafi
    try {
      const onboardedResp = await certificateApi.checkOnboarded(commonName)
      if (onboardedResp.data?.onboarded) {
        setVenafiLookup({
          status: 'error',
          exists: false,
          matches: [],
          truncated: false,
          certNotActive: false,
          alreadyOnboarded: true,
          error: onboardedResp.data.message || `A certificate with common name '${commonName}' has already been onboarded.`,
          selectedMatch: null,
        })
        return
      }
    } catch (err) {
      console.error('Failed to check onboarded status:', err)
      // Non-blocking — continue to Venafi search even if this check fails
    }

    try {
      const response = await certificateApi.checkVenafiExists(commonName, 25)
      const { exists, matches, truncated } = response.data || {}

      venafiLookupRef.current.lastQuery = commonName
      setVenafiLookup({
        status: 'success',
        exists: Boolean(exists),
        matches: Array.isArray(matches) ? matches : [],
        truncated: Boolean(truncated),
        error: ''
      })
    } catch (error) {
      const errorMessage = error?.response?.data?.detail || 'Failed to check Venafi'
      setVenafiLookup({
        status: 'error',
        exists: false,
        matches: [],
        truncated: false,
        error: errorMessage
      })
    }
  }

  // Check if common name is already onboarded (used for new cert flow on blur)
  const checkCommonNameOnboarded = async (commonName) => {
    const cn = (commonName || '').trim()
    if (!cn) {
      setCommonNameOnboarded({ checked: false, onboarded: false, message: '' })
      return
    }
    try {
      const resp = await certificateApi.checkOnboarded(cn)
      if (resp.data?.onboarded) {
        setCommonNameOnboarded({
          checked: true,
          onboarded: true,
          message: resp.data.message || `A certificate with common name '${cn}' has already been onboarded.`,
        })
      } else {
        setCommonNameOnboarded({ checked: true, onboarded: false, message: '' })
      }
    } catch (err) {
      console.error('Failed to check onboarded status:', err)
      setCommonNameOnboarded({ checked: false, onboarded: false, message: '' })
    }
  }

  useEffect(() => {
    // When selectVenafiMatch sets the full CN programmatically, skip this reset.
    // Only run when the user themselves changes the search text.
    if (skipCommonNameResetRef.current) {
      skipCommonNameResetRef.current = false
      return
    }
    venafiLookupRef.current.lastQuery = ''
    resetVenafiLookup()
    // Reset new-cert duplicate check when common name changes
    setCommonNameOnboarded({ checked: false, onboarded: false, message: '' })
  }, [formData.commonName])

  useEffect(() => {
    // When the user switches between new/existing cert modes, wipe everything
    // except their identity (emailId stays from the logged-in user).
    if (formData.preExisting === '') return // initial mount — nothing to reset
    venafiLookupRef.current.lastQuery = ''
    resetVenafiLookup()
    setSelectedProduct(null)
    setSelectedOnpremProduct(null)
    setOnpremProductExists(null)
    setAwsAccounts([])
    setCommonNameOnboarded({ checked: false, onboarded: false, message: '' })
    setFormData(prev => ({
      isAWS: '',
      productCode: '',
      productName: '',
      environment: '',
      emailId: prev._originalEmailId || prev.emailId,
      _originalEmailId: prev._originalEmailId,
      clientInvolvement: '',
      clientName: '',
      dependencyPresent: '',
      dependencies: [''],
      certificateUsage: '',
      venafiPolicyBase: '',
      commonName: '',
      venafiPath: '',
      sanEntries: [''],
      mutualAuth: '',
      layer7: '',
      layer7Endpoint: '',
      preExisting: prev.preExisting, // keep the value that just changed
      awsAdditionalFields: {
        accountName: '',
        accountNumber: '',
        applicationId: '',
        awsCertInOnprem: 'no',
        acmParameter: 'no',
        existingAcmArn1: '',
        existingAcmArn2: '',
        ssmParameter: 'no',
        ssmFormat: '',
        ssmCertPath: '',
        ssmPrivateKeyPath: '',
        ssmChainKeyPath: '',
        ssmCertPasswordPath: '',
        ssmCertContentType: '',
        ssmCertParamType: 'Advanced',
        ssmKeyContentType: '',
        ssmKeyParamType: '',
        ssmChainContentType: '',
        ssmChainParamType: 'Advanced',
        ssmPasswordContentType: '',
        ssmPasswordParamType: '',
        smParameter: 'no',
        smFormat: '',
        smCertPath: '',
      },
      onpremAdditionalFields: {
        publicCertKeyLocation: '',
        privateCertKeyLocation: '',
      },
    }))
  }, [formData.preExisting])
  const isPreExisting = formData.preExisting === 'true'

  const isStep1Complete = () => formData.preExisting !== ''
  
  // Step 2 for pre-existing: Venafi cert lookup (CN + cert selected + env + product)
  const isVenafiLookupStepComplete = () => {
    if (!isPreExisting) return true // N/A for new certs
    if (!venafiLookup.selectedMatch) return false
    if (venafiLookup.certNotActive) return false // Cert not yet active in Venafi
    if (!formData.commonName) return false
    if (!formData.environment) return false
    // Product must be either auto-matched or manually selected
    if (!formData.productCode && !formData.productName) return false
    return true
  }
  
  const isStep2Complete = () => formData.isAWS !== ''
  
  // Step 3: AWS or OnPrem Configuration
  const isStep3Complete = () => {
    if (formData.isAWS === 'true') {
      if (isPreExisting) {
        // For pre-existing, product and env are already set from Venafi lookup
        return formData.awsAdditionalFields.accountName !== '' && formData.environment !== ''
      }
      return formData.productCode !== '' && 
             formData.awsAdditionalFields.accountName !== '' && 
             formData.environment !== ''
    } else if (formData.isAWS === 'false') {
      if (isPreExisting) {
        // Product must be onboarded (DLs registered) before proceeding
        if (onpremProductExists === false) return false
        // For pre-existing, product and env are already set from Venafi lookup
        return formData.environment !== ''
      }
      // OnPrem: only need product name and environment (devices pulled from Venafi)
      return formData.productName !== '' && 
             formData.environment !== ''
    }
    return false
  }
  
  // Step 4 (or 5 for pre-existing): Contact and Certificate Information
  const isStep4Complete = () => {
    // Block if common name is already onboarded (new cert flow)
    if (!isPreExisting && commonNameOnboarded.onboarded) return false

    const commonNameValidation = validateCommonName()
    // certificateUsage and venafiPolicyBase are always required
    const certificateUsageValid = formData.certificateUsage !== ''
    const venafiPolicyBaseValid = formData.venafiPolicyBase !== ''
    
    // For pre-existing certs, clientInvolvement and dependencyPresent are not required
    const clientInvolvementValid = isPreExisting || formData.clientInvolvement !== ''
    const dependencyPresentValid = isPreExisting || formData.dependencyPresent !== ''

    const baseComplete = formData.emailId !== '' && 
                         formData.itsmGroup !== '' &&
                         clientInvolvementValid && 
                         dependencyPresentValid &&
                         certificateUsageValid &&
                         venafiPolicyBaseValid &&
                         formData.commonName !== '' &&
                         commonNameValidation.isValid
    
    if (!baseComplete) return false
    
    // Client name required if client involvement is true (only for new certs)
    if (formData.preExisting === 'false' && formData.clientInvolvement === 'true' && formData.clientName === '') return false
    
    // At least one dependency required if dependency present is true (only for new certs)
    if (formData.preExisting !== 'true' && formData.dependencyPresent === 'true' && 
        (!formData.dependencies || formData.dependencies.every(dep => dep.trim() === ''))) return false
    
    return true
  }
  
  // Step 5: mTLS and Layer7 Configuration
  const isStep5Complete = () => {
    if (formData.mutualAuth === '') return false
    
    // Layer7 only required if mutualAuth is yes
    if (formData.mutualAuth === 'yes') {
      if (formData.layer7 === '') return false
      // Layer7 endpoint required if layer7 is yes
      if (formData.layer7 === 'yes' && formData.layer7Endpoint === '') return false
    }
    
    // OnPrem mTLS cert locations required
    if (formData.isAWS === 'false' && formData.mutualAuth === 'yes') {
      if (formData.onpremAdditionalFields.publicCertKeyLocation === '' || 
          formData.onpremAdditionalFields.privateCertKeyLocation === '') return false
    }
    
    return true
  }
  
  // Step 6: AWS Certificate Storage Options (only for AWS)
  const isStep6Complete = () => {
    if (formData.isAWS !== 'true') return true // Not applicable for OnPrem
    
    if (formData.awsAdditionalFields.awsCertInOnprem === '' || 
        formData.awsAdditionalFields.acmParameter === '' || 
        formData.awsAdditionalFields.ssmParameter === '' || 
        formData.awsAdditionalFields.smParameter === '') return false
    
    // SSM Parameter validation
    if (formData.awsAdditionalFields.ssmParameter === 'yes') {
      if (formData.awsAdditionalFields.ssmFormat === '' || formData.awsAdditionalFields.ssmCertPath === '') return false
      
      // Certificate Content Type and Parameter Type are required
      if (formData.awsAdditionalFields.ssmCertContentType === '' || formData.awsAdditionalFields.ssmCertParamType === '') return false
      
      if (formData.awsAdditionalFields.ssmFormat === 'PEM') {
        if (formData.awsAdditionalFields.ssmPrivateKeyPath === '' || formData.awsAdditionalFields.ssmChainKeyPath === '') return false
        // Private Key and Chain Content Type and Parameter Type are required for PEM
        if (formData.awsAdditionalFields.ssmKeyContentType === '' || formData.awsAdditionalFields.ssmKeyParamType === '') return false
        if (formData.awsAdditionalFields.ssmChainContentType === '' || formData.awsAdditionalFields.ssmChainParamType === '') return false
      }
      
      if (formData.awsAdditionalFields.ssmFormat === 'PFX') {
        if (formData.awsAdditionalFields.ssmCertPasswordPath === '') return false
        // Password Content Type and Parameter Type are required for PFX
        if (formData.awsAdditionalFields.ssmPasswordContentType === '' || formData.awsAdditionalFields.ssmPasswordParamType === '') return false
      }
    }
    
    // Secrets Manager validation
    if (formData.awsAdditionalFields.smParameter === 'yes') {
      if (formData.awsAdditionalFields.smFormat === '' || formData.awsAdditionalFields.smCertPath === '') return false
    }

    // ACM ARN 1 is required for pre-existing certs when ACM is enabled
    if (formData.awsAdditionalFields.acmParameter === 'yes' && formData.preExisting === 'true') {
      if (!formData.awsAdditionalFields.existingAcmArn1) return false
    }

    return true
  }

  // Helper function to check if all mandatory fields are filled
  const isFormComplete = () => {
    if (!isStep1Complete()) return false
    if (isPreExisting && !isVenafiLookupStepComplete()) return false
    if (!isStep2Complete()) return false
    if (!isStep3Complete()) return false
    if (!isStep4Complete()) return false
    if (!isStep5Complete()) return false
    if (!isStep6Complete()) return false
    return true
  }

  // Auto-fill email from localStorage on component mount
  useEffect(() => {
    const userEmail = localStorage.getItem('mail')
    if (userEmail) {
      setFormData(prev => ({
        ...prev,
        emailId: userEmail,
        _originalEmailId: userEmail,   // stash so clearVenafiSelection can restore it
      }))
    }
  }, [])

  useEffect(() => {
    // Load all ITSM products on component mount
    fetchItsmProducts()
  }, [])

  // Debounced search for products
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchText !== undefined) {
        fetchItsmProducts(productSearchText)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [productSearchText])

  // Load AWS accounts when product is selected
  useEffect(() => {
    if (formData.productCode) {
      loadOnboardedAccountsForProduct(formData.productCode)
    } else {
      setAwsAccounts([])
    }
  }, [formData.productCode])

  const fetchItsmProducts = async (search = '') => {
    setLoadingProducts(true)
    try {
      const response = await itsmProductApi.getAll({ search, limit: 100 })
      setItsmProducts(response.data.items || [])
    } catch (error) {
      console.error('Error loading ITSM products:', error)
      setSnackbar({ open: true, message: 'Error loading products', severity: 'error' })
    } finally {
      setLoadingProducts(false)
    }
  }

  const loadOnboardedAccountsForProduct = async (productCode) => {
    try {
      setLoadingAccounts(true)
      const response = await awsCertAccountApi.getOnboardedAccountsByProduct(productCode)
      const accounts = response.data.items || []
      
      // Each item now has product_env, so we can display account with environment
      setAwsAccounts(accounts)
    } catch (error) {
      console.error('Error loading AWS accounts:', error)
      setSnackbar({ open: true, message: 'Error loading AWS accounts for this product', severity: 'error' })
      setAwsAccounts([])
    } finally {
      setLoadingAccounts(false)
    }
  }

  const checkProductDlExists = async (productCode) => {
    try {
      const response = await productDlApi.getAll({ product_code: productCode, limit: 1 })
      const exists = response.data.items && response.data.items.length > 0
      setOnpremProductExists(exists)
      return exists
    } catch (error) {
      console.error('Error checking product_dls:', error)
      setOnpremProductExists(null)
      return null
    }
  }

  const handleOnpremProductChange = async (newProduct) => {
    setSelectedOnpremProduct(newProduct)
    setFormData({ ...formData, productName: newProduct?.product_name || '', productCode: newProduct?.product_code || '' })
    setOnpremProductExists(null) // Reset while checking
    
    if (newProduct && newProduct.product_code) {
      await checkProductDlExists(newProduct.product_code)
    }
  }

  const handleProductOnboardComplete = (product) => {
    setProductOnboardModalOpen(false)
    setOnpremProductExists(true)
    setSnackbar({ open: true, message: 'Product onboarded successfully! You can now continue with certificate onboarding.', severity: 'success' })
  }

  const determineAvailableEnvironments = (accountName, accountEnvironments) => {
    // If we have specific environments from the onboarded account, use those
    if (accountEnvironments && accountEnvironments.length > 0) {
      return accountEnvironments
    }
    
    // Otherwise fall back to account name-based detection
    const lowerAccountName = accountName.toLowerCase()
    
    if (lowerAccountName.includes('-dev') || lowerAccountName.includes('_dev')) {
      return ['dev']
    } else if (lowerAccountName.includes('-qa') || lowerAccountName.includes('_qa')) {
      return ['qa']
    } else if (lowerAccountName.includes('-uat') || lowerAccountName.includes('_uat')) {
      return ['uat']
    } else if (lowerAccountName.includes('-prd') || lowerAccountName.includes('-prod') || 
               lowerAccountName.includes('_prd') || lowerAccountName.includes('_prod')) {
      return ['prd']
    }
    
    return ['dev', 'qa', 'uat', 'prd']
  }

  // Reset AWS/OnPrem specific fields when deployment location changes
  // Track previous isAWS value to prevent clearing on re-render/HMR
  const prevIsAWSRef = useRef(formData.isAWS)

  useEffect(() => {
    // Only run reset logic when isAWS actually changes, not on initial render or HMR
    if (prevIsAWSRef.current === formData.isAWS) {
      return
    }
    prevIsAWSRef.current = formData.isAWS

    if (formData.isAWS === 'true') {
      // Clear on-prem fields when switching to AWS
      // For pre-existing certs, preserve product/env — they come from Venafi lookup
      setFormData(prev => ({
        ...prev,
        ...(prev.preExisting !== 'true' && {
          productCode: '',
          productName: '',
          environment: '',
        }),
        onpremAdditionalFields: {
          publicCertKeyLocation: '',
          privateCertKeyLocation: '',
        },
      }))
      setSelectedOnpremProduct(null)
      setOnpremProductExists(null)
    } else if (formData.isAWS === 'false') {
      // Clear AWS fields when switching to on-prem
      // For pre-existing certs, preserve product/env — they come from Venafi lookup
      if (formData.preExisting !== 'true') {
        setSelectedProduct(null)
      }
      setFormData(prev => ({
        ...prev,
        ...(prev.preExisting !== 'true' && {
          productCode: '',
          productName: '',
          environment: '',
        }),
        awsAdditionalFields: {
          accountName: '',
          accountNumber: '',
          applicationId: '',
          awsCertInOnprem: 'no',
          acmParameter: 'no',
          ssmParameter: 'no',
          ssmFormat: '',
          ssmCertPath: '',
          ssmPrivateKeyPath: '',
          ssmChainKeyPath: '',
          ssmCertPasswordPath: '',
          ssmCertContentType: '',
          ssmCertParamType: '',
          ssmKeyContentType: '',
          ssmKeyParamType: '',
          ssmChainContentType: '',
          ssmChainParamType: '',
          ssmPasswordContentType: '',
          ssmPasswordParamType: '',
          smParameter: 'no',
          smFormat: '',
          smCertPath: '',
        },
      }))
    }
  }, [formData.isAWS])

  // When On-Premises is selected and a product is already known (pre-existing flow),
  // check whether that product's distribution lists have been registered.
  useEffect(() => {
    if (formData.isAWS === 'false' && formData.productCode) {
      checkProductDlExists(formData.productCode)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isAWS, formData.productCode])

  // Auto-set Client Involvement based on Certificate Usage for new certs
  useEffect(() => {
    if (formData.preExisting === 'false' && formData.certificateUsage) {
      // Client involvement is true for client certs and "other"
      const requiresClientInvolvement = [
        'client-internal',
        'client-broadridge',
        'client-external',
        'other'
      ].includes(formData.certificateUsage)
      
      setFormData(prev => ({
        ...prev,
        clientInvolvement: requiresClientInvolvement ? 'true' : 'false',
        // Clear client name if involvement becomes false
        clientName: requiresClientInvolvement ? prev.clientName : ''
      }))
    }
  }, [formData.certificateUsage, formData.preExisting])

  // Reset client name when client involvement changes (only for new certs)
  useEffect(() => {
    if (formData.preExisting === 'false' && formData.clientInvolvement === 'false') {
      setFormData(prev => ({
        ...prev,
        clientName: '',
      }))
    }
  }, [formData.clientInvolvement, formData.preExisting])

  // Reset dependency fields when dependency present changes
  useEffect(() => {
    if (formData.dependencyPresent === 'false') {
      setFormData(prev => ({
        ...prev,
        dependency1: '',
        dependency2: '',
        dependency3: '',
        dependency4: '',
      }))
    }
  }, [formData.dependencyPresent])

  // Reset layer7 fields when mutualAuth changes to no
  useEffect(() => {
    if (formData.mutualAuth === 'no') {
      setFormData(prev => ({
        ...prev,
        layer7: '',
        layer7Endpoint: '',
      }))
    }
  }, [formData.mutualAuth])

  // Reset layer7 endpoint when layer7 changes
  useEffect(() => {
    if (formData.layer7 === 'no') {
      setFormData(prev => ({
        ...prev,
        layer7Endpoint: '',
      }))
    }
  }, [formData.layer7])

  // Reset SSM fields when SSM parameter changes
  useEffect(() => {
    if (formData.awsAdditionalFields.ssmParameter === 'no') {
      setFormData(prev => ({
        ...prev,
        awsAdditionalFields: {
          ...prev.awsAdditionalFields,
          ssmFormat: '',
          ssmCertPath: '',
          ssmPrivateKeyPath: '',
          ssmChainKeyPath: '',
          ssmCertPasswordPath: '',
          ssmCertContentType: '',
          ssmCertParamType: '',
          ssmKeyContentType: '',
          ssmKeyParamType: '',
          ssmChainContentType: '',
          ssmChainParamType: '',
          ssmPasswordContentType: '',
          ssmPasswordParamType: '',
        },
      }))
    }
  }, [formData.awsAdditionalFields.ssmParameter])

  // Reset SSM format-specific fields when format changes
  useEffect(() => {
    if (formData.awsAdditionalFields.ssmFormat === 'PEM') {
      setFormData(prev => ({
        ...prev,
        awsAdditionalFields: {
          ...prev.awsAdditionalFields,
          ssmCertPasswordPath: '',
          ssmPasswordContentType: '',
          ssmPasswordParamType: '',
          ssmCertContentType: 'String',
          ssmCertParamType: 'Standard',
          ssmKeyContentType: 'String',
          ssmKeyParamType: 'Standard',
          ssmChainContentType: 'String',
          ssmChainParamType: 'Advanced',
        },
      }))
    } else if (formData.awsAdditionalFields.ssmFormat === 'PFX') {
      setFormData(prev => ({
        ...prev,
        awsAdditionalFields: {
          ...prev.awsAdditionalFields,
          ssmPrivateKeyPath: '',
          ssmChainKeyPath: '',
          ssmKeyContentType: '',
          ssmKeyParamType: '',
          ssmChainContentType: '',
          ssmChainParamType: '',
          ssmCertContentType: 'String',
          ssmCertParamType: 'Advanced',
          ssmPasswordContentType: 'String',
          ssmPasswordParamType: 'Standard',
        },
      }))
    }
  }, [formData.awsAdditionalFields.ssmFormat])

  // Reset Secrets Manager fields when SM parameter changes
  useEffect(() => {
    if (formData.awsAdditionalFields.smParameter === 'no') {
      setFormData(prev => ({
        ...prev,
        awsAdditionalFields: {
          ...prev.awsAdditionalFields,
          smFormat: '',
          smCertPath: '',
        },
      }))
    }
  }, [formData.awsAdditionalFields.smParameter])

  // Update SSM and Secrets Manager paths when commonName changes
  useEffect(() => {
    if (formData.commonName) {
      const sanitizedName = formData.commonName.replace(/[^a-zA-Z0-9._-]/g, '_')
      const sdlc = formData.environment ? formData.environment.toLowerCase() : ''
      
      setFormData(prev => {
        const updates = {}
        
        // Update SSM paths if SSM is enabled
        if (prev.awsAdditionalFields.ssmParameter === 'yes') {
          const fmt = prev.awsAdditionalFields.ssmFormat.toLowerCase()
          updates.ssmCertPath = sdlc ? `/venafi/certificates/${sdlc}/${sanitizedName}/${fmt}/cert` : ''
          
          if (prev.awsAdditionalFields.ssmFormat === 'PEM') {
            updates.ssmPrivateKeyPath = sdlc ? `/venafi/certificates/${sdlc}/${sanitizedName}/${fmt}/key` : ''
            updates.ssmChainKeyPath = sdlc ? `/venafi/certificates/${sdlc}/${sanitizedName}/${fmt}/chain` : ''
          } else if (prev.awsAdditionalFields.ssmFormat === 'PFX') {
            updates.ssmCertPasswordPath = sdlc ? `/venafi/certificates/${sdlc}/${sanitizedName}/${fmt}/password` : ''
          }
        }
        
        // Update Secrets Manager paths if SM is enabled
        if (prev.awsAdditionalFields.smParameter === 'yes') {
          updates.smCertPath = sdlc ? `/venafi/certificates/${sdlc}/${sanitizedName}` : ''
        }
        
        // Only update if there are changes to apply
        if (Object.keys(updates).length > 0) {
          return {
            ...prev,
            awsAdditionalFields: {
              ...prev.awsAdditionalFields,
              ...updates
            }
          }
        }
        
        return prev
      })
    }
  }, [formData.commonName, formData.awsAdditionalFields.ssmParameter, formData.awsAdditionalFields.ssmFormat, formData.awsAdditionalFields.smParameter, formData.awsAdditionalFields.smFormat])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Prevent double submission
    if (submitting) return
    
    // Validation
    if (!formData.preExisting) {
      setSnackbar({ open: true, message: 'Please indicate if certificate is pre-existing', severity: 'error' })
      return
    }

    if (!formData.isAWS) {
      setSnackbar({ open: true, message: 'Please select AWS or On-Premises', severity: 'error' })
      return
    }

    if (!formData.commonName) {
      setSnackbar({ open: true, message: 'Common Name is required', severity: 'error' })
      return
    }

    const commonNameValidation = validateCommonName()
    if (!commonNameValidation.isValid) {
      setSnackbar({ open: true, message: commonNameValidation.message, severity: 'error' })
      return
    }

    if (!formData.emailId) {
      setSnackbar({ open: true, message: 'Email ID is required', severity: 'error' })
      return
    }

    setSubmitting(true)
    
    try {
      // Prepare data for backend onboard API (matches CertificateOnboardRequest schema)
      const certificateData = {
        // Required fields
        common_name: formData.commonName,
        product_code: formData.productCode,
        environment: formData.environment,
        email_id: formData.emailId,
        pre_existing: formData.preExisting,
        is_aws: formData.isAWS,
        certificate_usage: formData.certificateUsage,
        itsm_group: formData.itsmGroup || '',
        venafi_policy_base: formData.venafiPolicyBase,
        
        // Optional fields
        venafi_path: formData.venafiPath || '',
        san_entries: formData.sanEntries.filter(san => san.trim() !== ''),
        mutual_auth: formData.mutualAuth || 'no',
        layer7: formData.layer7 || 'no',
        layer7_endpoint: formData.layer7Endpoint || null,
        // For pre-existing certs, determine client_involvement from whether client_name exists
        // For new certs, use the user's selection
        client_involvement: formData.preExisting === 'true' 
          ? (formData.clientName && formData.clientName !== 'N/A' ? 'true' : 'false')
          : (formData.clientInvolvement || 'false'),
        client_name: formData.clientName || null,
        dependency_present: formData.dependencyPresent || 'false',
        dependencies: formData.dependencies.filter(d => d && d.trim() !== ''),
        
        // AWS-specific fields (if applicable)
        aws_additional_fields: formData.isAWS === 'true' ? {
          accountName: formData.awsAdditionalFields.accountName,
          accountNumber: formData.awsAdditionalFields.accountNumber,
          applicationId: formData.awsAdditionalFields.applicationId,
          awsCertInOnprem: formData.awsAdditionalFields.awsCertInOnprem,
          acmParameter: formData.awsAdditionalFields.acmParameter,
          existingAcmArn1: formData.awsAdditionalFields.existingAcmArn1 || '',
          existingAcmArn2: formData.awsAdditionalFields.existingAcmArn2 || '',
          ssmParameter: formData.awsAdditionalFields.ssmParameter,
          ssmFormat: formData.awsAdditionalFields.ssmFormat,
          ssmCertPath: formData.awsAdditionalFields.ssmCertPath,
          ssmPrivateKeyPath: formData.awsAdditionalFields.ssmPrivateKeyPath,
          ssmChainKeyPath: formData.awsAdditionalFields.ssmChainKeyPath,
          ssmCertPasswordPath: formData.awsAdditionalFields.ssmCertPasswordPath,
          ssmCertContentType: formData.awsAdditionalFields.ssmCertContentType,
          ssmCertParamType: formData.awsAdditionalFields.ssmCertParamType,
          ssmKeyContentType: formData.awsAdditionalFields.ssmKeyContentType,
          ssmKeyParamType: formData.awsAdditionalFields.ssmKeyParamType,
          ssmChainContentType: formData.awsAdditionalFields.ssmChainContentType,
          ssmChainParamType: formData.awsAdditionalFields.ssmChainParamType,
          ssmPasswordContentType: formData.awsAdditionalFields.ssmPasswordContentType,
          ssmPasswordParamType: formData.awsAdditionalFields.ssmPasswordParamType,
          smParameter: formData.awsAdditionalFields.smParameter,
          smFormat: formData.awsAdditionalFields.smFormat,
          smCertPath: formData.awsAdditionalFields.smCertPath,
        } : null,

        // Pass the user-selected Venafi cert DN/GUID so the backend skips the
        // ambiguous search when multiple certs share the same common name.
        venafi_cert_dn: formData.preExisting === 'true' ? (venafiLookup.selectedMatch?.certificate_dn || null) : null,
        venafi_guid:    formData.preExisting === 'true' ? (venafiLookup.selectedMatch?.guid || null) : null,
        contact_email_cr: venafiLookup.selectedMatch?.contact_email_cr || null,
      }

      console.log('Submitting certificate onboarding to Venafi:', certificateData)
      
      // Call the backend onboard API (creates in Venafi + stores in Secrets Manager + DB)
      const response = await certificateApi.onboard(certificateData)
      
      console.log('Certificate onboarded successfully:', response.data)
      setSnackbar({ 
        open: true, 
        message: `Certificate onboarded successfully! Certificate ID: ${response.data.cert_id}`, 
        severity: 'success' 
      })
      
      // Reset form after successful submission
      setTimeout(() => {
        certDetailsCache.current = {} // clear cert detail cache on successful onboard
        const userEmail = localStorage.getItem('mail') || ''
        setFormData({
          preExisting: '',
          isAWS: '',
          productCode: '',
          productName: '',
          environment: '',
          emailId: userEmail,
          clientInvolvement: '',
          clientName: '',
          dependencyPresent: '',
          dependencies: [''],
          certificateUsage: '',
          venafiPolicyBase: '',
          commonName: '',
          venafiPath: '',
          sanEntries: [''],
          mutualAuth: '',
          layer7: '',
          layer7Endpoint: '',
          awsAdditionalFields: {
            accountName: '',
            accountNumber: '',
            applicationId: '',
            awsCertInOnprem: 'no',
            acmParameter: 'no',
            ssmParameter: 'no',
            ssmFormat: '',
            ssmCertPath: '',
            ssmPrivateKeyPath: '',
            ssmChainKeyPath: '',
            ssmCertPasswordPath: '',
            ssmCertContentType: '',
            ssmCertParamType: '',
            ssmKeyContentType: '',
            ssmKeyParamType: '',
            ssmChainContentType: '',
            ssmChainParamType: '',
            ssmPasswordContentType: '',
            ssmPasswordParamType: '',
            smParameter: 'no',
            smFormat: '',
            smCertPath: '',
          },
          onpremAdditionalFields: {
            publicCertKeyLocation: '',
            privateCertKeyLocation: '',
          },
        })
        setSelectedProduct(null)
        setSelectedOnpremProduct(null)
      }, 2000)
    } catch (error) {
      console.error('Error onboarding certificate:', error)
      const detail = error.response?.data?.detail
      const errorMessage = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Error onboarding certificate. Please try again.')
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue) // Store the full product object
    setFormData({
      ...formData,
      productCode: newValue?.product_code || '',
      productName: newValue?.product_name || '',
      // For pre-existing certs, environment comes from Venafi — don't wipe it when the
      // user manually picks a product that didn't auto-match.
      environment: formData.preExisting === 'true' ? formData.environment : '',
      awsAdditionalFields: {
        ...formData.awsAdditionalFields,
        accountName: '',
        accountNumber: '',
        applicationId: '',
      },
    })
    // When on-prem is already selected, check if the newly picked product has DLs registered
    if (formData.isAWS === 'false') {
      if (newValue?.product_code) {
        checkProductDlExists(newValue.product_code)
      } else {
        setOnpremProductExists(null)
      }
    }
  }

  const handleAccountChange = (event) => {
    const selectedAccount = awsAccounts.find(acc => acc.account_name === event.target.value)
    const accountName = event.target.value
    
    // Determine available environments based on account's onboarded environments
    const envs = determineAvailableEnvironments(accountName, selectedAccount?.environments || [])
    setAvailableEnvironments(envs)
    
    // Auto-select environment if only one option
    const newEnv = envs.length === 1 ? envs[0] : ''
    
    setFormData({
      ...formData,
      environment: newEnv,
      venafiPolicyBase: '', // Reset policy path when environment changes
      awsAdditionalFields: {
        ...formData.awsAdditionalFields,
        accountName: accountName,
        accountNumber: selectedAccount?.account_number || '',
        applicationId: selectedAccount?.application_id || '',
      },
    })
  }

  // Modal handlers for AWS Account Onboarding
  const handleModalOpen = () => {
    setSelectedProductForModal(null)
    setUseAvailableAccountsOnly(false)
    setModalOpen(true)
  }

  const handleModalOpenWithProduct = () => {
    if (!selectedProduct) {
      console.error('No product selected')
      return
    }
    console.log('Opening modal with product:', selectedProduct)
    setSelectedProductForModal(selectedProduct)
    setUseAvailableAccountsOnly(true)
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setSelectedProductForModal(null)
    setUseAvailableAccountsOnly(false)
  }

  const handleOnboardSuccess = () => {
    // Reload accounts for the selected product
    if (formData.productCode) {
      loadOnboardedAccountsForProduct(formData.productCode)
    }
  }

  const addSanEntry = () => {
    setFormData({
      ...formData,
      sanEntries: [...formData.sanEntries, '']
    })
  }

  const updateSanEntry = (index, value) => {
    const newSanEntries = [...formData.sanEntries]
    newSanEntries[index] = value
    setFormData({
      ...formData,
      sanEntries: newSanEntries
    })
  }

  const removeSanEntry = (index) => {
    const newSanEntries = formData.sanEntries.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      sanEntries: newSanEntries.length > 0 ? newSanEntries : ['']
    })
  }

  // For pre-existing certs: Step 2 is Venafi Lookup (complete → show deployment)
  // Deployment location visible once Venafi lookup is complete (pre-existing) or step 1 is done (new)  
  const isDeploymentVisible = isPreExisting ? isVenafiLookupStepComplete() : formData.preExisting !== ''

  // Contact & Cert Info visible once AWS/OnPrem config is complete.
  // For pre-existing on-prem, also require the ITSM product to be onboarded (has DLs).
  const isContactStepVisible = isPreExisting
    ? (formData.isAWS === 'true'
        ? formData.awsAdditionalFields.accountName !== ''
        : formData.isAWS === 'false' && onpremProductExists !== false)
    : (formData.isAWS === 'true' 
        ? (formData.productCode !== '' && formData.awsAdditionalFields.accountName !== '' && formData.environment !== '')
        : (formData.isAWS === 'false' && formData.productName !== '' && formData.environment !== ''))

  // Dynamic step numbers
  const stepOffset = isPreExisting ? 1 : 0
  const deploymentStepNum = 2 + stepOffset
  const configStepNum = 3 + stepOffset
  const contactStepNum = 4 + stepOffset
  const mtlsStepNum = 5 + stepOffset
  const storageStepNum = 6 + stepOffset

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
          Certificate Onboarding
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          This form adapts to your selections. You can change any previous answer at any time, 
          and the form will automatically update to reflect your choices.
        </Alert>
        
        <form onSubmit={handleSubmit} noValidate>
          {/* Step 1: Pre-existing Certificate */}
          <CertificateStatusStep
            formData={formData}
            setFormData={setFormData}
            isComplete={isStep1Complete()}
          />

          {/* Step 2 (pre-existing only): Venafi Certificate Lookup */}
          <VenafiCertLookupStep
            formData={formData}
            setFormData={setFormData}
            isComplete={isVenafiLookupStepComplete()}
            checkVenafiCommonName={checkVenafiCommonName}
            venafiLookup={venafiLookup}
            selectVenafiMatch={selectVenafiMatch}
            recheckVenafiMatch={recheckVenafiMatch}
            clearVenafiSelection={clearVenafiSelection}
            itsmProducts={itsmProducts}
            loadingProducts={loadingProducts}
            productSearchText={productSearchText}
            setProductSearchText={setProductSearchText}
            selectedProduct={selectedProduct}
            handleProductChange={handleProductChange}
            stepNumber={2}
          />

          {/* Step 2/3: AWS or On-Prem */}
          {isDeploymentVisible && (
            <DeploymentLocationStep
              formData={formData}
              setFormData={setFormData}
              isComplete={isStep2Complete()}
              stepNumber={deploymentStepNum}
            />
          )}

          {/* Step 3/4: AWS Configuration */}
          <AwsConfigurationStep
            formData={formData}
            setFormData={setFormData}
            isComplete={isStep3Complete()}
            itsmProducts={itsmProducts}
            loadingProducts={loadingProducts}
            selectedProduct={selectedProduct}
            handleProductChange={handleProductChange}
            productSearchText={productSearchText}
            setProductSearchText={setProductSearchText}
            awsAccounts={awsAccounts}
            loadingAccounts={loadingAccounts}
            handleModalOpenWithProduct={handleModalOpenWithProduct}
            stepNumber={configStepNum}
          />

          {/* Step 3/4: On-Prem Configuration */}
          <OnPremConfigurationStep
            formData={formData}
            setFormData={setFormData}
            isComplete={isStep3Complete()}
            itsmProducts={itsmProducts}
            loadingProducts={loadingProducts}
            selectedOnpremProduct={selectedOnpremProduct}
            handleOnpremProductChange={handleOnpremProductChange}
            onpremProductExists={onpremProductExists}
            productSearchText={productSearchText}
            setProductSearchText={setProductSearchText}
            setProductOnboardModalOpen={setProductOnboardModalOpen}
            stepNumber={configStepNum}
          />

          {/* Step 4/5: Contact and Certificate Information */}
          <ContactCertInfoStep
            formData={formData}
            setFormData={setFormData}
            isComplete={isStep4Complete()}
            isVisible={isContactStepVisible}
            checkVenafiCommonName={checkVenafiCommonName}
            checkCommonNameOnboarded={checkCommonNameOnboarded}
            commonNameOnboarded={commonNameOnboarded}
            venafiLookup={venafiLookup}
            selectVenafiMatch={selectVenafiMatch}
            addSanEntry={addSanEntry}
            updateSanEntry={updateSanEntry}
            removeSanEntry={removeSanEntry}
            stepNumber={contactStepNum}
          />

          {/* Step 5/6: mTLS and Layer7 Configuration */}
          <MtlsLayer7Step
            formData={formData}
            setFormData={setFormData}
            isComplete={isStep5Complete()}
            isVisible={isContactStepVisible && isStep4Complete()}
            stepNumber={mtlsStepNum}
          />

          {/* Step 6/7: AWS Certificate Storage Options */}
          {formData.isAWS === 'true' && (
            <AwsStorageOptionsStep
              formData={formData}
              setFormData={setFormData}
              isComplete={isStep6Complete()}
              stepNumber={storageStepNum}
            />
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => {
                const userEmail = localStorage.getItem('mail') || ''
                setFormData({
                  preExisting: '',
                  isAWS: '',
                  productCode: '',
                  productName: '',
                  environment: '',
                  emailId: userEmail,
                  clientInvolvement: '',
                  clientName: '',
                  dependencyPresent: '',
                  dependencies: [''],
                  certificateUsage: '',
                  venafiPolicyBase: '',
                  commonName: '',
                  venafiPath: '',
                  sanEntries: [''],
                  mutualAuth: '',
                  layer7: '',
                  layer7Endpoint: '',
                  awsAdditionalFields: {
                    accountName: '',
                    accountNumber: '',
                    applicationId: '',
                    awsCertInOnprem: 'no',
                    acmParameter: 'no',
                    ssmParameter: 'no',
                    ssmFormat: '',
                    ssmCertPath: '',
                    ssmPrivateKeyPath: '',
                    ssmChainKeyPath: '',
                    ssmCertPasswordPath: '',
                    ssmCertContentType: '',
                    ssmCertParamType: '',
                    ssmKeyContentType: '',
                    ssmKeyParamType: '',
                    ssmChainContentType: '',
                    ssmChainParamType: '',
                    ssmPasswordContentType: '',
                    ssmPasswordParamType: '',
                    smParameter: 'no',
                    smFormat: '',
                    smCertPath: '',
                    smPrivateKeyPath: '',
                    smChainKeyPath: '',
                    smCertPasswordPath: '',
                  },
                  onpremAdditionalFields: {
                    publicCertKeyLocation: '',
                    privateCertKeyLocation: '',
                  },
                })
                setSelectedProduct(null)
                setSelectedOnpremProduct(null)
                setOnpremProductExists(null)
                setProductSearchText('')
                setAwsAccounts([])
                setAvailableEnvironments(['dev', 'qa', 'uat', 'prd'])
              }}
            >
              Reset
            </Button>
            {isFormComplete() && (
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                endIcon={!submitting ? <SendIcon /> : null}
              >
                {submitting ? 'Onboarding Certificate...' : 'Submit Onboarding'}
              </Button>
            )}
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* AWS Account Onboarding Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={handleModalClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon color="primary" />
            <Typography variant="h6">AWS Account Onboarding</Typography>
          </Box>
          <IconButton onClick={handleModalClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <AccountOnboard 
            key={`${modalOpen}-${selectedProductForModal?.product_code || 'new'}`}
            preSelectedProduct={selectedProductForModal}
            useAvailableAccountsOnly={useAvailableAccountsOnly}
            onSuccess={handleOnboardSuccess}
            isModal={true}
          />
        </DialogContent>
      </Dialog>

      {/* Product Onboarding Modal */}
      <Dialog
        open={productOnboardModalOpen}
        onClose={() => setProductOnboardModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">Product Onboarding Required</Typography>
          </Box>
          <IconButton onClick={() => setProductOnboardModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            Before onboarding certificates for this product, we need to register the product and its team distribution lists.
          </Alert>
          <ProductOnboard 
            key={`${productOnboardModalOpen}-${selectedOnpremProduct?.product_code || selectedProduct?.product_code || 'new'}`}
            prePopulatedProductName={selectedOnpremProduct?.product_name || (isPreExisting ? selectedProduct?.product_name : null)}
            onComplete={handleProductOnboardComplete}
          />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default CertificateOnboard
